// src/pages/About.jsx
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

function About() {
  const timelineRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe timeline items
    const timelineItems = timelineRef.current?.querySelectorAll('.timeline-item');
    timelineItems?.forEach((item) => observer.observe(item));

    // Observe cards
    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const timeline = [
    {
      year: '2024 - Present',
      role: 'Junior Fintech App Developer',
      company: 'Tangent Solutions Inc.',
      description: 'Developing POS systems handling ₱10M+ daily transactions, implementing EMV payment integrations, and building secure fintech solutions with Java Spring Boot.',
      achievements: [
        'Built Tangent Debit & Cashout POS systems',
        'Implemented QR PH backend with Spring Boot',
        'Ensured PCI DSS compliance',
        'Managed AWS/Azure deployments'
      ]
    },
    {
      year: '2022 - 2024',
      role: 'Full Stack Developer',
      company: 'ScaleUp Works',
      description: 'Created comprehensive web and mobile solutions for various clients, from water billing systems to real estate platforms.',
      achievements: [
        'Developed Water Billing System with Flutter & React',
        'Built Pilates Studio platform with WooCommerce integration',
        'Created Property Insights app with real-time alerts',
        'Managed PostgreSQL & MySQL databases'
      ]
    },
    {
      year: '2019 - 2023',
      role: 'BS Computer Engineering',
      company: 'Bicol University',
      description: 'Graduated with honors while serving as Student Research Associate in Machine Learning and Automation.',
      achievements: [
        'Student Research Associate',
        'Machine Learning & Automation focus',
        'Academic excellence in Computer Engineering',
        'Hands-on experience with FPGA & IoT'
      ]
    }
  ];

  return (
    <section className="about-section">
      <div className="about-container">
        {/* Header */}
        <div className="about-header">
          <span className="about-subtitle">Get to Know Me</span>
          <h2 className="about-title">About Me</h2>
          <div className="title-divider"></div>
        </div>

        {/* Hero Introduction */}
        <div className="about-hero">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Hi, I'm <span className="gradient-text">James Galos</span>
              </h1>
              <p className="hero-subtitle">
                Full Stack Developer & Fintech Specialist
              </p>
              <p className="hero-description">
                I'm a passionate developer with expertise in building secure, scalable applications. 
                With a unique philosophy of "rarely saying no," I tackle challenges head-on by learning 
                whatever tech stack is needed to deliver exceptional results.
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number">₱10M+</span>
                  <span className="stat-label">Daily Transactions Handled</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">15+</span>
                  <span className="stat-label">Technologies Mastered</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">3+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
              </div>
            </div>
            <div className="hero-visual">
              <div className="visual-card">
                <div className="code-snippet">
                  <div className="code-line">
                    <span className="code-keyword">const</span> developer = {'{'}
                  </div>
                  <div className="code-line indent">
                    name: <span className="code-string">'James Galos'</span>,
                  </div>
                  <div className="code-line indent">
                    role: <span className="code-string">'Full Stack Developer'</span>,
                  </div>
                  <div className="code-line indent">
                    expertise: [<span className="code-string">'Fintech'</span>, <span className="code-string">'Mobile'</span>, <span className="code-string">'Web'</span>],
                  </div>
                  <div className="code-line indent">
                    philosophy: <span className="code-string">'Learn, Build, Deliver'</span>
                  </div>
                  <div className="code-line">{'}'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Timeline */}
        <div className="timeline-section">
          <h3 className="section-title">Professional Journey</h3>
          <div className="timeline" ref={timelineRef}>
            {timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <div className="marker-dot"></div>
                </div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-year">{item.year}</span>
                    <h4 className="timeline-role">{item.role}</h4>
                    <p className="timeline-company">{item.company}</p>
                  </div>
                  <p className="timeline-description">{item.description}</p>
                  <ul className="timeline-achievements">
                    {item.achievements.map((achievement, i) => (
                      <li key={i}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Grid */}
        <div className="skills-section">
          <h3 className="section-title">Technical Arsenal</h3>
          <div className="skills-grid">
            <div 
              className="skill-category" 
              ref={el => cardsRef.current[0] = el}
            >
              <div className="category-header">
                <div className="category-icon">
                  <span>🎨</span>
                </div>
                <h4>Frontend</h4>
              </div>
              <div className="tech-stack">
                <span className="tech-item">React.js</span>
                <span className="tech-item">Next.js</span>
                <span className="tech-item">Flutter</span>
                <span className="tech-item">TypeScript</span>
                <span className="tech-item">Tailwind CSS</span>
              </div>
            </div>

            <div 
              className="skill-category" 
              ref={el => cardsRef.current[1] = el}
            >
              <div className="category-header">
                <div className="category-icon">
                  <span>⚙️</span>
                </div>
                <h4>Backend</h4>
              </div>
              <div className="tech-stack">
                <span className="tech-item">Java Spring Boot</span>
                <span className="tech-item">Node.js</span>
                <span className="tech-item">Python Flask</span>
                <span className="tech-item">Express.js</span>
                <span className="tech-item">Laravel</span>
              </div>
            </div>

            <div 
              className="skill-category" 
              ref={el => cardsRef.current[2] = el}
            >
              <div className="category-header">
                <div className="category-icon">
                  <span>💾</span>
                </div>
                <h4>Database & Cloud</h4>
              </div>
              <div className="tech-stack">
                <span className="tech-item">PostgreSQL</span>
                <span className="tech-item">MySQL</span>
                <span className="tech-item">Firebase</span>
                <span className="tech-item">AWS</span>
                <span className="tech-item">Azure</span>
              </div>
            </div>

            <div 
              className="skill-category" 
              ref={el => cardsRef.current[3] = el}
            >
              <div className="category-header">
                <div className="category-icon">
                  <span>💳</span>
                </div>
                <h4>Fintech & Security</h4>
              </div>
              <div className="tech-stack">
                <span className="tech-item">EMV Integration</span>
                <span className="tech-item">PCI DSS</span>
                <span className="tech-item">Bancnet</span>
                <span className="tech-item">QR PH</span>
                <span className="tech-item">Payment APIs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Interests Section */}
        <div className="interests-section">
          <h3 className="section-title">Beyond The Code</h3>
          <div className="interests-grid">
            <div className="interest-card">
              <div className="interest-emoji">🏃‍♂️</div>
              <h4>Fitness Enthusiast</h4>
              <p>Running & Cycling to maintain work-life balance</p>
            </div>
            <div className="interest-card">
              <div className="interest-emoji">🤖</div>
              <h4>AI & Automation</h4>
              <p>Exploring Edge-AI and Machine Learning projects</p>
            </div>
            <div className="interest-card">
              <div className="interest-emoji">🔧</div>
              <h4>Hardware Tinkering</h4>
              <p>Building with Arduino, FPGA, and IoT devices</p>
            </div>
            <div className="interest-card">
              <div className="interest-emoji">📚</div>
              <h4>Continuous Learning</h4>
              <p>Always exploring new technologies and methodologies</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="about-cta">
          <h3>Let's Build Something Amazing Together</h3>
          <p>I'm always excited to work on new challenges and innovative projects</p>
          <div className="cta-buttons">
            <Link to="/contact" className="cta-primary">Get In Touch</Link>
            <Link to="/projects" className="cta-secondary">View My Work</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;